import moment from 'moment';
import {Types} from 'mongoose';
import {LessonMoveTeacherMode} from '@/enums/course-manager/LessonMoveTeacherMode.enum';
import {LessonMoveTeacherCheckResponse} from '@/classes/dto/course-manager/request/lessonMove/LessonMoveTeacherCheck.request.dto';
import {LessonMoveTeacherImpactItem} from '@/classes/dto/course-manager/request/lessonMove/LessonMoveTeacherImpact.request.dto';
import {LessonMoveTeacherDecisionTemplate} from '@/classes/dto/course-manager/request/lessonMove/LessonMoveTeacherDecision.template';

/**
 * "Wer hält die verschobene Stunde?" (feat/moved-lesson-teacher), pure helpers without API.
 * User decision: "Es bekommt immer der das Geld, der den Kurs hält". When a moved lesson's original and new date fall
 * under different teachers, the user chooses: original teacher / teacher at the new date (default) / another teacher.
 * The answer is stored as the teacher override of the move. Without an answer the lesson is "Lehrkraft ungeklärt" and
 * billed to nobody.
 */

export type CrossingChoice = 'original' | 'new' | 'other';

type Crossing = Pick<LessonMoveTeacherCheckResponse, 'originalTeacher' | 'originalTeacherName' | 'newDateTeacher' | 'newDateTeacherName'>;

const idOf = (value: unknown): string => {
    if (!value) {
        return '';
    }
    if (typeof value === 'object' && (value as { _id?: unknown })._id) {
        return String((value as { _id: unknown })._id);
    }
    return String(value);
};

const toId = (value: unknown): Types.ObjectId | null => (idOf(value) ? new Types.ObjectId(idOf(value)) : null);

export function originalLabel(crossing: Crossing): string {
    return crossing.originalTeacherName || (crossing.originalTeacher ? 'ursprüngliche Lehrkraft' : 'keine Lehrkraft');
}

export function newDateLabel(crossing: Crossing): string {
    return crossing.newDateTeacherName || (crossing.newDateTeacher ? 'Lehrkraft am neuen Termin' : 'keine Lehrkraft (vakant)');
}

/** "Am 22.10. ist Herr B für den Kurs zuständig, die ursprüngliche Stunde hatte Frau A." */
export function crossingStatement(crossing: Crossing, newStart: Date | string): string {
    const day = moment(newStart, ['YYYY-MM-DD HH:mm', moment.ISO_8601]).format('DD.MM.');
    const target = crossing.newDateTeacher || crossing.newDateTeacherName
        ? `ist ${crossing.newDateTeacherName || 'eine andere Lehrkraft'} für den Kurs zuständig`
        : 'ist keine Lehrkraft für den Kurs eingetragen';
    const original = crossing.originalTeacher || crossing.originalTeacherName
        ? `die ursprüngliche Stunde hatte ${crossing.originalTeacherName || 'eine andere Lehrkraft'}`
        : 'die ursprüngliche Stunde hatte keine Lehrkraft';
    return `Am ${day} ${target}, ${original}.`;
}

export function crossingQuestion(crossing: Crossing, newStart: Date | string): string {
    return `${crossingStatement(crossing, newStart)} Wer hält die Stunde?`;
}

export function crossingOptions(crossing: Crossing): Array<{ value: CrossingChoice; text: string }> {
    return [
        {value: 'original', text: `${originalLabel(crossing)} (ursprüngliche Lehrkraft)`},
        {value: 'new', text: `${newDateLabel(crossing)} (Lehrkraft am neuen Termin)`},
        {value: 'other', text: 'Andere Lehrkraft'},
    ];
}

/** Teacher of the choice; null = vacant; undefined = "other" without a selection */
export function crossingTeacherOf(crossing: Crossing, choice: CrossingChoice, otherTeacher: unknown): Types.ObjectId | null | undefined {
    if (choice === 'original') {
        return toId(crossing.originalTeacher);
    }
    if (choice === 'new') {
        return toId(crossing.newDateTeacher);
    }
    return toId(otherTeacher) ?? undefined;
}

/** Preselection: an existing decision (override) of the move, otherwise the teacher at the new date */
export function crossingChoiceOf(crossing: Crossing, move?: { teacherMode?: string; teacher?: unknown; teacherDecisionPending?: boolean } | null): {
    choice: CrossingChoice;
    teacherId?: Types.ObjectId | null;
} {
    if (!move || move.teacherMode !== LessonMoveTeacherMode.OVERRIDE || move.teacherDecisionPending) {
        return {choice: 'new'};
    }
    const teacher = idOf(move.teacher);
    if (teacher && teacher === idOf(crossing.originalTeacher)) {
        return {choice: 'original'};
    }
    if (teacher === idOf(crossing.newDateTeacher)) {
        return {choice: 'new'};
    }
    return {choice: 'other', teacherId: toId(move.teacher)};
}

/* ------------------------------------------------------------------ bulk (teacher change dialogs) */

export type ImpactChoice = { choice: CrossingChoice; teacherId: Types.ObjectId | null };

export function impactCountText(count: number): string {
    return count === 1 ? '1 verschobene Stunde betroffen' : `${count} verschobene Stunden betroffen`;
}

export function impactLessonText(item: Pick<LessonMoveTeacherImpactItem, 'courseCustomId' | 'originalDate' | 'newStart'>): string {
    return `${item.courseCustomId}: Stunde vom ${moment(item.originalDate).format('DD.MM.')} verschoben auf ${moment(item.newStart).format('dd, DD.MM. HH:mm')} Uhr`;
}

/** Default of the bulk list: teacher at the new date */
export function defaultImpactChoices(items: LessonMoveTeacherImpactItem[]): Record<string, ImpactChoice> {
    return Object.fromEntries(items.map((item) => [idOf(item.lessonMove), {choice: 'new' as CrossingChoice, teacherId: null}]));
}

/** Decisions for the update; an "other" choice without teacher is not complete (null) */
export function impactDecisions(items: LessonMoveTeacherImpactItem[], choices: Record<string, ImpactChoice>): LessonMoveTeacherDecisionTemplate[] | null {
    const decisions: LessonMoveTeacherDecisionTemplate[] = [];
    for (const item of items) {
        const choice = choices[idOf(item.lessonMove)] ?? {choice: 'new', teacherId: null};
        const teacher = crossingTeacherOf(item, choice.choice, choice.teacherId);
        if (teacher === undefined) {
            return null;
        }
        decisions.push({lessonMove: new Types.ObjectId(idOf(item.lessonMove)), teacher});
    }
    return decisions;
}
